import React from 'react';
import Callout from 'src/components/InformationCallout/Callout';
import LinkPreviewCard from 'src/components/LinkPreviewCard/LinkPreviewCard';
import CalcomConnectionBadge from 'src/components/CalcomConnectionBadge/CalcomConnectionBadge';
import { ICalcomConnectedCalendar } from '../../types';
import calcomConnectCalendarPreview from '../../assets/calcom-connect-calendar-preview.png';
import googleLogo from '../../provider-logos/google.svg';
import outlookLogo from '../../provider-logos/outlook.svg';
import appleLogo from '../../provider-logos/apple.svg';
import exchangeLogo from '../../provider-logos/exchange.svg';
import genericCalendarLogo from '../../provider-logos/generic-calendar.svg';
import './connected-calendars.scss';

interface IProviderLogo {
  match: string;
  logo: string;
}

interface ConnectedCalendarsProps {
  calendars: ICalcomConnectedCalendar[];
  // Trims the explanatory blurb and the add/remove guide callout — used inside the event
  // dialog, where the surrounding copy already covers the "how" and we only need the list.
  compact?: boolean;
  // Heading above the list. Override to phrase it for the surrounding context (e.g. "Availability
  // is checked against…") instead of repeating a separate lead line outside this component.
  title?: string;
}

// Cal.com integration slugs vary ('google-calendar', 'office365_calendar', 'apple_calendar', …),
// so match on a substring. Anything unmatched (CalDAV, ICS, Zoho, Lark, Vimcal, …) falls back to a
// neutral calendar glyph until a real brand asset is added.
const PROVIDER_LOGOS: IProviderLogo[] = [
  { match: 'google', logo: googleLogo },
  { match: 'office365', logo: outlookLogo },
  { match: 'outlook', logo: outlookLogo },
  { match: 'exchange', logo: exchangeLogo },
  { match: 'apple', logo: appleLogo },
];

const resolveProviderLogo = (provider: string): string => {
  const key: string = provider.toLowerCase();
  return (
    PROVIDER_LOGOS.find((entry: IProviderLogo) => key.includes(entry.match))
      ?.logo ?? genericCalendarLogo
  );
};

const ConnectedCalendars = ({
  calendars,
  compact = false,
  title = 'Connected calendars',
}: ConnectedCalendarsProps) => {
  return (
    <div className="connected-calendars">
      <p className="connected-calendars-label">{title}</p>
      {!compact && calendars.length > 0 && (
        <p className="connected-calendars-blurb">
          These are the calendars you&apos;ve added to your connected Cal.com
          account. Cal.com checks them for your real availability — when a
          walkthrough or key-pickup booking link is used, it only offers times
          you&apos;re free across these calendars and writes the confirmed
          booking back to them, so Juniper scheduling never clashes with
          what&apos;s already on your calendar.
        </p>
      )}

      {calendars.length === 0 ? (
        <div className="connected-calendars-empty">
          <CalcomConnectionBadge isConnected={false} />
          <span className="connected-calendars-empty-detail">
            No calendars connected yet. Link a Google or Outlook calendar in
            Cal.com to enable availability.
          </span>
        </div>
      ) : (
        <div className="calendar-list">
          {calendars.map((calendar: ICalcomConnectedCalendar) => (
            <div
              key={`${calendar.provider}-${calendar.email}`}
              className="calendar-row"
            >
              <span className="calendar-logo-tile">
                <img
                  className="calendar-logo"
                  src={resolveProviderLogo(calendar.provider)}
                  alt=""
                />
              </span>
              <div className="calendar-meta">
                <span className="calendar-title">{calendar.title}</span>
                {calendar.email && (
                  <span className="calendar-email">{calendar.email}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!compact && (
        <Callout
          variant="info"
          title="Want to add or remove calendars to sync with Juniper?"
          subtitle="Connecting and disconnecting calendars is done in Cal.com, not here — this guide walks you through it."
        >
          <LinkPreviewCard
            href="https://cal.com/help/quick-start/calendar"
            title="Connect your calendars"
            description="Link Google, Outlook, Apple, and more to your Cal.com account so availability stays in sync."
            siteName="Cal.com Help"
            image={calcomConnectCalendarPreview}
          />
        </Callout>
      )}
    </div>
  );
};

export default ConnectedCalendars;